import { createGlobalState } from "@vueuse/core";
// eslint-disable-next-line import/no-named-as-default
import posthog from "posthog-js";
import { ref } from "vue";
import { Logger } from "@/core/utilities";
import type { IPageExperimentContext, IPostHogConfig } from "../types";
import type { CaptureResult, JsonType } from "posthog-js";

const POSTHOG_FLAG_TIMEOUT_MS = 3000;

export const usePostHog = createGlobalState(() => {
  const initialized = ref(false);
  const config = ref<IPostHogConfig | null>(null);

  let _experimentContext: IPageExperimentContext | null = null;
  let _hasConsent = false;

  const setExperimentContext = (context: IPageExperimentContext | null) => {
    _experimentContext = context;
  };

  const getExperimentContext = () => _experimentContext;

  const init = (rawConfigJson: string | null) => {
    if (!rawConfigJson) {
      Logger.debug("PostHog: No config found, skipping initialization.");
      return;
    }

    let parsed: IPostHogConfig;
    try {
      parsed = JSON.parse(rawConfigJson) as IPostHogConfig;
    } catch {
      Logger.error("PostHog: Failed to parse PostHog:Config JSON.");
      return;
    }

    if (!parsed.apiKey) {
      Logger.debug("PostHog: No apiKey in config, skipping initialization.");
      return;
    }

    // .cn domain guard
    if (window.location.hostname.includes(".cn")) {
      Logger.debug("PostHog: .cn domain detected, skipping initialization.");
      return;
    }

    // Prerender/bot guard
    if (/Prerender/i.test(navigator.userAgent) || (window as unknown as Record<string, unknown>).__PRERENDER_INJECTED) {
      Logger.debug("PostHog: Prerender/bot detected, skipping initialization.");
      return;
    }

    config.value = parsed;

    // Build bootstrap from localStorage (for return visits)
    const bootstrapFlags: Record<string, string | boolean> = {};
    const bootstrapPayloads: Record<string, JsonType> = {};
    const storageKey = `ph_${parsed.apiKey}_posthog`;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const data = JSON.parse(stored) as {
          $enabled_feature_flags?: Record<string, string | boolean>;
          $feature_flag_payloads?: Record<string, JsonType>;
        };
        if (data.$enabled_feature_flags) {
          Object.assign(bootstrapFlags, data.$enabled_feature_flags);
        }
        if (data.$feature_flag_payloads) {
          Object.assign(bootstrapPayloads, data.$feature_flag_payloads);
        }
      }
    } catch {
      // localStorage unavailable or corrupted — proceed without bootstrap
    }

    // Only provide bootstrap when there are actual cached flags — passing an empty
    // bootstrap object tells PostHog flags are "already loaded" and fires onFeatureFlags
    // immediately with an empty set, preventing the real /decide response from being awaited.
    const hasBootstrapData = Object.keys(bootstrapFlags).length > 0;

    posthog.init(parsed.apiKey, {
      api_host: parsed.host || "https://us.i.posthog.com",
      // Use memory persistence until consent — no cookies/localStorage pre-consent.
      // This still allows the /decide call for feature flag evaluation.
      debug: !!parsed.debug,
      persistence: "memory",
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      ...(hasBootstrapData && {
        bootstrap: {
          featureFlags: bootstrapFlags,
          featureFlagPayloads: bootstrapPayloads,
        },
      }),
      before_send: (event: CaptureResult | null) => {
        if (!event) {
          return null;
        }

        // Always allow $feature_flag_called through — PostHog needs this to attribute
        // users to experiment groups. Contains no PII (just flag name + value).
        if (event.event === "$feature_flag_called") {
          return event;
        }

        // Block all other events until OneTrust C0004 consent is granted
        if (!_hasConsent) {
          return null;
        }

        try {
          if (event.event === "$pageleave" && _experimentContext) {
            event.properties = {
              ...event.properties,
              scroll_count: _experimentContext.scrollCount,
              max_section_reached: _experimentContext.maxSectionReached,
              reached_footer: _experimentContext.reachedFooter,
              page: _experimentContext.page,
              flag_name: _experimentContext.flagName,
              variant: _experimentContext.variant,
            };
          }
        } catch {
          // Never break PostHog's event pipeline
        }

        return event;
      },
    });

    initialized.value = true;

    // Force feature flag reload — ensures /decide call is made regardless of internal state
    posthog.reloadFeatureFlags();

    // OneTrust consent observation
    observeOneTrustConsent();
  };

  const observeOneTrustConsent = () => {
    const enableCapture = () => {
      _hasConsent = true;
      // Upgrade to persistent storage and enable autocapture + pageleave
      posthog.set_config({
        persistence: "localStorage+cookie",
        autocapture: true,
        capture_pageleave: true,
      });
    };

    const checkConsent = () => {
      const cookieValue = document.cookie.split("; ").find((row) => row.startsWith("OptanonConsent="));
      if (cookieValue?.includes("C0004")) {
        enableCapture();
        return true;
      }
      return false;
    };

    // Check immediately in case consent already granted
    if (checkConsent()) {
      return;
    }

    // Observe cookie changes via OptanonWrapper callback (OneTrust fires this on consent change)
    const originalWrapper = window.OptanonWrapper;
    window.OptanonWrapper = () => {
      if (originalWrapper) {
        originalWrapper();
      }
      checkConsent();
    };
  };

  const getFeatureFlag = (flagName: string): string | boolean | undefined => {
    if (!initialized.value) {
      return undefined;
    }
    return posthog.getFeatureFlag(flagName) ?? undefined;
  };

  const getFeatureFlagPayload = (flagName: string): unknown => {
    if (!initialized.value) {
      return undefined;
    }
    return posthog.getFeatureFlagPayload(flagName);
  };

  const waitForFeatureFlag = (flagName: string): Promise<string | boolean | undefined> => {
    return new Promise((resolve) => {
      if (!initialized.value) {
        resolve(undefined);
        return;
      }

      // Check if flag is already available (bootstrap case)
      const existingValue = posthog.getFeatureFlag(flagName);
      if (existingValue !== undefined) {
        resolve(existingValue);
        return;
      }

      // Wait for flags to load with timeout
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve(undefined);
        }
      }, POSTHOG_FLAG_TIMEOUT_MS);

      // onFeatureFlags may fire immediately if flags were "loaded" during init with empty
      // data (persistence: "memory" without bootstrap). Keep waiting for /decide if the
      // flag is still undefined — only resolve when we get a real value or timeout.
      posthog.onFeatureFlags(() => {
        if (resolved) {
          return;
        }
        const val = posthog.getFeatureFlag(flagName) ?? undefined;
        if (val !== undefined) {
          resolved = true;
          clearTimeout(timeout);
          resolve(val);
        }
      });
    });
  };

  const captureEvent = (eventName: string, properties?: Record<string, unknown>) => {
    if (!initialized.value) {
      return;
    }
    posthog.capture(eventName, properties);
  };

  return {
    initialized,
    config,
    init,
    getFeatureFlag,
    getFeatureFlagPayload,
    waitForFeatureFlag,
    captureEvent,
    setExperimentContext,
    getExperimentContext,
  };
});
